import type { Item, ItemId } from '../types.js';

/**
 * "2× Long Island" is one line on the receipt but two drinks at the table,
 * and "me and Carla had one Long Island" is about one of them. So before a
 * sentence is parsed, a line with quantity N becomes N units — i3#1, i3#2 —
 * each priced at the unit price, each addressable on its own. "The Long
 * Islands" still selects all of them through the category or the plural.
 *
 * Lines longer than `maxUnits` stay whole: nobody splits thirty beers one
 * by one, and the prompt would balloon.
 */
export function expandUnits(items: Item[], maxUnits = 8): Item[] {
  return items.flatMap((item) => {
    if (item.qty <= 1 || item.qty > maxUnits || !Number.isInteger(item.qty)) return [item];
    // Unit prices may not divide the line evenly (3 × 3.33 = 10.00): give the
    // remainder to the first units so the sum is still the printed line.
    const base = Math.floor(item.lineTotal / item.qty);
    let remainder = item.lineTotal - base * item.qty;
    return Array.from({ length: item.qty }, (_, i) => {
      const extra = remainder > 0 ? 1 : 0;
      remainder -= extra;
      const price = (base + extra) as Item['lineTotal'];
      return {
        id: unitId(item.id, i + 1),
        name: `${item.name} (${i + 1} of ${item.qty})`,
        qty: 1,
        unitPrice: price,
        lineTotal: price,
        category: item.category,
        unitOf: { itemId: item.id, index: i + 1, count: item.qty },
      };
    });
  });
}

export const unitId = (itemId: ItemId, index: number): ItemId => `${itemId}#${index}`;

/** The receipt line a unit came from, or the id itself. */
export const baseItemId = (id: ItemId): ItemId => id.split('#')[0] ?? id;
